import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface IProps {
  collapsed: boolean;
  onToggle: () => void;
}

const doubleClickWindowMs = 400;
const hintDelayMs = 200;
const railOverlapPx = 4;
const seamWidthPx = 20;

export default function SidebarSeam({ collapsed, onToggle }: IProps) {
  const [hint, setHint] = useState<{ x: number; y: number }>();
  const lastMouseDown = useRef(0);
  const hintPosition = useRef({ x: 0, y: 0 });
  const hintTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(hintTimer.current), []);

  const overToggle = (e: React.MouseEvent) => !!(e.target as HTMLElement).closest('.sidebar-seam-toggle');

  const hideHint = () => {
    clearTimeout(hintTimer.current);
    hintTimer.current = undefined;
    setHint(undefined);
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || overToggle(e)) return;
    const previous = lastMouseDown.current;
    const isDoubleClick = previous > 0 && e.timeStamp - previous < doubleClickWindowMs;
    lastMouseDown.current = isDoubleClick ? 0 : e.timeStamp;
    if (isDoubleClick) onToggle();
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (overToggle(e)) {
      hideHint();
      return;
    }
    hintPosition.current = { x: e.clientX, y: e.clientY };
    if (hint) {
      setHint(hintPosition.current);
    } else if (!hintTimer.current) {
      hintTimer.current = setTimeout(() => {
        hintTimer.current = undefined;
        setHint({ ...hintPosition.current });
      }, hintDelayMs);
    }
  };

  const action = collapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <div
      data-testid="sidebar-seam"
      className="sidebar-seam position-fixed d-none d-sm-flex align-items-center justify-content-center user-select-none"
      style={{
        top: 0,
        bottom: 0,
        left: collapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
        width: seamWidthPx,
        marginLeft: -railOverlapPx,
        zIndex: 150,
        cursor: 'pointer',
        transition: 'left 0.2s ease-in-out',
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseLeave={hideHint}>
      <button
        className="sidebar-seam-toggle position-absolute d-flex align-items-center justify-content-center p-0 bg-white border rounded-circle shadow-sm"
        style={{ width: '34px', height: '34px', left: railOverlapPx, top: '50%', transform: 'translate(-50%, -50%)' }}
        onClick={onToggle}
        aria-label={action}
        title={action}>
        <i className={`bi-chevron-${collapsed ? 'right' : 'left'}`}></i>
      </button>
      {hint && createPortal(
        <span
          className="position-fixed bg-white border rounded text-muted shadow-sm"
          style={{
            left: hint.x,
            top: hint.y + 16,
            transform: 'translateX(-50%)',
            padding: '0.125rem 0.375rem',
            fontSize: '0.6875rem',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 153,
          }}>
          Double-click to {collapsed ? 'expand' : 'collapse'}
        </span>,
        document.body)}
    </div>
  );
}
